// src/components/modals/InvoiceDetailsModal.jsx
import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { invoiceAPI, handleApiError } from '../../services/api'
import { showErrorToast } from '../../utils/notificationUtils'

/**
 * Read-only invoice detail: line items (what services happened), payments
 * received, credit notes, and the totals block. Fetches on open.
 *
 * Props:
 *   isOpen, onClose, invoiceId, currency
 */
const InvoiceDetailsModal = ({ isOpen, onClose, invoiceId, currency = '' }) => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir() === 'rtl'

  const [invoice, setInvoice] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen || !invoiceId) return
    let cancelled = false
    const fetchDetail = async () => {
      setLoading(true)
      setInvoice(null)
      try {
        const res = await invoiceAPI.getInvoice(invoiceId)
        if (!cancelled) setInvoice(res.data?.data || null)
      } catch (err) {
        if (!cancelled) {
          showErrorToast(handleApiError(err) || t('billing.details.loadError', 'Failed to load invoice details'))
          onClose()
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchDetail()
    return () => { cancelled = true }
  }, [isOpen, invoiceId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    if (isOpen) window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const money = (v) => `${Number(v || 0).toLocaleString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`
  const fmtDate = (d) => {
    if (!d) return '-'
    try {
      return new Date(d).toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    } catch { return d }
  }

  const overlay = {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  }
  const card = {
    backgroundColor: '#fff', borderRadius: '16px', padding: '26px 28px',
    maxWidth: '760px', width: '100%', maxHeight: '88vh', overflowY: 'auto',
    fontFamily: '"Red Hat Display", sans-serif',
  }
  const th = {
    textAlign: 'start', fontSize: '11px', textTransform: 'uppercase', color: '#6B7280',
    fontWeight: 700, padding: '8px 10px', borderBottom: '1px solid #E5E7EB',
  }
  const td = { padding: '9px 10px', fontSize: '13px', color: '#111827', borderBottom: '1px solid #F3F4F6' }

  const statusKey = invoice && (invoice.refunded_at
    ? 'refunded'
    : (invoice.balance ?? 1) <= 0 ? 'paid' : (invoice.paid ?? 0) > 0 ? 'partial' : 'unpaid')
  const statusPill = {
    refunded: { bg: '#F3F4F6', color: '#6B7280' },
    paid: { bg: '#D1FAE5', color: '#065F46' },
    partial: { bg: '#FEF3C7', color: '#92400E' },
    unpaid: { bg: '#FEE2E2', color: '#991B1B' },
  }[statusKey]

  const buyerLine = invoice
    ? (invoice.buyer_name || invoice.client?.name || (invoice.buyer_phone ? '' : t('billing.details.walkIn', 'Walk-in')))
    : ''

  return createPortal(
    <div style={overlay} onClick={onClose}>
      <div style={card} dir={isRTL ? 'rtl' : 'ltr'} onClick={(e) => e.stopPropagation()}>
        {loading || !invoice ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
            {t('billing.details.loading', 'Loading invoice…')}
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#111827' }}>
                    {invoice.invoice_number}
                  </h2>
                  <span style={{ background: statusPill.bg, color: statusPill.color, fontSize: '11px', fontWeight: 700, borderRadius: '10px', padding: '3px 10px' }}>
                    {t(`billing.status.${statusKey}`, statusKey)}
                  </span>
                </div>
                <div style={{ fontSize: '13px', color: '#6B7280' }}>
                  {t('billing.details.issued', 'Issued {{date}}', { date: fmtDate(invoice.issued_at) })}
                  {buyerLine && ` · ${buyerLine}`}
                  {invoice.buyer_phone && ` · ${invoice.buyer_phone}`}
                </div>
              </div>
              <button onClick={onClose} style={{
                border: 'none', background: '#F3F4F6', borderRadius: '8px', width: '32px', height: '32px',
                cursor: 'pointer', fontSize: '16px', color: '#6B7280', lineHeight: 1,
              }}>×</button>
            </div>

            {/* Line items */}
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>
              {t('billing.details.itemsTitle', 'Services / Items')}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
              <thead>
                <tr>
                  <th style={th}>#</th>
                  <th style={th}>{t('billing.details.description', 'Description')}</th>
                  <th style={{ ...th, textAlign: 'center' }}>{t('billing.details.qty', 'Qty')}</th>
                  <th style={{ ...th, textAlign: 'end' }}>{t('billing.details.unitPrice', 'Unit price')}</th>
                  <th style={{ ...th, textAlign: 'center' }}>{t('billing.details.vat', 'VAT %')}</th>
                  <th style={{ ...th, textAlign: 'end' }}>{t('billing.details.discount', 'Discount')}</th>
                  <th style={{ ...th, textAlign: 'end' }}>{t('billing.details.lineTotal', 'Total')}</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.items || []).map((item, i) => (
                  <tr key={i}>
                    <td style={td}>{i + 1}</td>
                    <td style={td}>{item.description}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{item.qty}</td>
                    <td style={{ ...td, textAlign: 'end' }}>{money(item.unit_price)}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{item.tax_rate > 0 ? `${item.tax_rate}%` : '0'}</td>
                    <td style={{ ...td, textAlign: 'end', color: item.discount_amount > 0 ? '#DC2626' : '#6B7280' }}>
                      {item.discount_amount > 0 ? `− ${money(item.discount_amount)}` : '-'}
                    </td>
                    <td style={{ ...td, textAlign: 'end', fontWeight: 700 }}>{money(item.line_total)}</td>
                  </tr>
                ))}
                {(invoice.items || []).length === 0 && (
                  <tr><td colSpan="7" style={{ ...td, textAlign: 'center', color: '#9CA3AF' }}>{t('billing.details.noItems', 'No line items')}</td></tr>
                )}
              </tbody>
            </table>

            {/* Totals */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '18px' }}>
              <table style={{ minWidth: '230px', borderCollapse: 'collapse', fontSize: '13px' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '3px 8px', color: '#4B5563' }}>{t('billing.details.subtotal', 'Subtotal')}</td>
                    <td style={{ padding: '3px 0', textAlign: 'end', fontWeight: 600 }}>{money(invoice.subtotal)}</td>
                  </tr>
                  {invoice.discount_total > 0 && (
                    <tr>
                      <td style={{ padding: '3px 8px', color: '#4B5563' }}>{t('billing.details.discountTotal', 'Discount')}</td>
                      <td style={{ padding: '3px 0', textAlign: 'end', fontWeight: 600, color: '#DC2626' }}>− {money(invoice.discount_total)}</td>
                    </tr>
                  )}
                  <tr>
                    <td style={{ padding: '3px 8px', color: '#4B5563' }}>{t('billing.details.tax', 'VAT')}</td>
                    <td style={{ padding: '3px 0', textAlign: 'end', fontWeight: 600 }}>{money(invoice.tax_total)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '5px 8px', fontWeight: 700, borderTop: '2px solid #111827', color: '#111827' }}>{t('billing.details.total', 'Total')}</td>
                    <td style={{ padding: '5px 0', textAlign: 'end', fontWeight: 700, borderTop: '2px solid #111827' }}>{money(invoice.total)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '3px 8px', color: '#4B5563' }}>{t('billing.details.paid', 'Paid')}</td>
                    <td style={{ padding: '3px 0', textAlign: 'end', fontWeight: 600 }}>{money(invoice.paid)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '3px 8px', color: '#B91C1C', fontWeight: 700 }}>{t('billing.details.remaining', 'Remaining')}</td>
                    <td style={{ padding: '3px 0', textAlign: 'end', fontWeight: 700, color: '#B91C1C' }}>{money(invoice.balance)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Payments */}
            {(invoice.payments || []).length > 0 && (
              <>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>
                  {t('billing.details.paymentsTitle', 'Payments')}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px' }}>
                  <thead>
                    <tr>
                      <th style={th}>{t('billing.details.date', 'Date')}</th>
                      <th style={th}>{t('billing.details.method', 'Method')}</th>
                      <th style={th}>{t('billing.details.notes', 'Notes')}</th>
                      <th style={{ ...th, textAlign: 'end' }}>{t('billing.details.amount', 'Amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.payments.map((p) => (
                      <tr key={p.id}>
                        <td style={td}>{fmtDate(p.paid_at)}</td>
                        <td style={td}>{p.method || '-'}</td>
                        <td style={{ ...td, color: '#6B7280' }}>{p.notes || '-'}</td>
                        <td style={{ ...td, textAlign: 'end', fontWeight: 600 }}>{money(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Credit notes */}
            {(invoice.credit_notes || []).length > 0 && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', padding: '10px 14px', fontSize: '12.5px', color: '#991B1B' }}>
                <div style={{ fontWeight: 700, marginBottom: '4px' }}>{t('billing.details.creditNotesTitle', 'Credit notes')}</div>
                {invoice.credit_notes.map((cn) => (
                  <div key={cn.id}>
                    {cn.invoice_number} · {money(cn.total)}
                    {cn.refund_reason ? ` — ${cn.refund_reason}` : ''}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  )
}

export default InvoiceDetailsModal
